'use client';
import base, { AxiosInstance } from 'axios';
import { createContext, useContext, useMemo, useState } from 'react';
import { AuthContext, clearStorage } from './Authentication';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import { Iconify } from '@/components/common/iconify';
import { UI_ICONS } from '@/lib/ui-icons';
import { getMainApiOrigin, getSelfOriginForRedirects } from '@/config/apiConfig';
import { extractApiMessage } from './Axios';
import { getActiveTenantHeaders, resolveActiveRequestUserId } from '@/lib/activeTenantScope';

export const AnalyticsAxiosContext = createContext<AxiosInstance | null>(null);

export const isCancel = base.isCancel;

/** Analytics API base URL. Replaces legacy Lambda; never use lambda-url for anon-visitor. */
const ANALYTICS_API_BASE = 'https://analytics.staging-api.boostt.ai';
const LEGACY_LAMBDA_ANON_VISITOR = 'https://ksbjsdamdhvt7hkioqpbro4ngy0dlqqx.lambda-url.us-east-2.on.aws';

function getAnalyticsBaseUrl() {
  const env = process.env.NEXT_PUBLIC_ANALYTICS_API_URL;
  if (!env) return ANALYTICS_API_BASE;
  const trimmed = env.replace(/\/+$/, '');
  if (trimmed === LEGACY_LAMBDA_ANON_VISITOR) return ANALYTICS_API_BASE;
  return trimmed;
}

function getAnalyticsInstance(auth, setSessionExpiredOpen) {
  const axios = base.create({
    baseURL: getAnalyticsBaseUrl(),
    withCredentials: true, // send cookies so analytics API (e.g. /api/anon-visitor) auth matches
  });

  const auth_exclude: Array<string> = [];

  axios.interceptors.request.use((req) => {
    const activeUserId = resolveActiveRequestUserId(auth.userId);
    if (!req.data) {
      req.data = {};
    }
    if (!req.data.userID && activeUserId) {
      if (req.data instanceof FormData) {
        req.data.append('userID', String(activeUserId));
      } else {
        req.data.userID = activeUserId;
      }
    }
    if (auth.isAuthenticated && auth_exclude.indexOf(req.url!) == -1) {
      if (!req.data.session_token) {
        req.data.session_token = auth.token;
      }
    }
    req.headers['cookies'] = document.cookie;
    req.headers['X-Frontend-Page'] = window.location.href;
    Object.assign(req.headers, getActiveTenantHeaders());

    return req;
  });

  axios.interceptors.response.use(
    (res) => res,
    (err) => {
      if (window.navigator.onLine && err.response) {
        if (err.response.status == 401) {
          const mainOrigin = getMainApiOrigin();
          if (
            auth.isAuthenticated &&
            err.response.config.url != `${mainOrigin}/api/logout`
          ) {
            location.href = `${getSelfOriginForRedirects()}/v2/login`;
          } else if (err.response.config.url == `${mainOrigin}/api/userLogin`) {
            location.href = `${getSelfOriginForRedirects()}/account-disabled`;
          } else {
            setSessionExpiredOpen(true);
          }
        } else {
          const status = err.response.status;
          const apiMessage = extractApiMessage(err.response?.data);
          const isProd = process.env.NODE_ENV === 'production';
          err.errorCode = status;
          err.formattedError = (isProd && status >= 500 ? null : apiMessage) || `Request failed with status ${status}`;
        }
      } else {
        err.formattedError =
          'An error occurred while connecting to the network';
      }
      return Promise.reject(err);
    }
  );
  return axios;
}

export default function AnalyticsAxios({ children }) {
  const authData = useContext(AuthContext);
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);

  // Initialize client immediately with current auth data
  const auth = authData || {};
  const client = useMemo(() => getAnalyticsInstance(auth, setSessionExpiredOpen), [authData]);

  const handleSessionExpiredClose = () => {
    setSessionExpiredOpen(false);
    clearStorage();
    location.href = `${getSelfOriginForRedirects()}/v2/login`;
  };

  return (
    <>
      <AnalyticsAxiosContext.Provider value={client}>{children}</AnalyticsAxiosContext.Provider>

      {/* Session Expired Dialog */}
      <Dialog
        open={sessionExpiredOpen}
        onClose={handleSessionExpiredClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Iconify icon={UI_ICONS.warning} sx={{ color: 'warning.main' }} />
            <Typography variant="h6" component="div">
              Session Expired
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Your session has expired. Please log in again to continue.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSessionExpiredClose}
            variant="contained"
            color="primary"
            fullWidth
          >
            Go to Login
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

